import React from 'react';
import { Alert as MuiAlert, AlertProps as MuiAlertProps, AlertTitle } from '@mui/material';
import { Icon } from '../Icon';
import { Heading } from '../Heading';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { IconButton } from '../IconButton';
import { Collapse } from '../Collapse';
import {
  PounceStyleProps,
  extractPounceProps,
  mapPouncePropsToSx,
  useDisclosure,
} from '../../utils';
import type { IconType } from '../../theme/icons';
import { OverflowTextWithViewMore } from '../OverflowTextWithViewMore';

type AlertVariant = 'success' | 'info' | 'warning' | 'error' | 'suggest' | 'default';

export interface AlertProps
  extends Omit<MuiAlertProps, 'variant' | 'severity' | 'color' | 'title'>, PounceStyleProps {
  /** The main text of the alert */
  title?: React.ReactNode;

  /** The style of the Alert */
  variant?: AlertVariant;

  /** The background style of the Alert */
  variantBackgroundStyle?: 'solid' | 'transparent';

  /** A secondary text to further explain the title */
  description?: React.ReactNode;

  /** A react component containing available actions for the Alert */
  actions?: React.ReactNode | ((helpers: { onClose: () => void }) => React.ReactNode);

  /** Whether the Alert should have a close button in order to remove itself */
  discardable?: boolean;

  /** Whether the Alert is wrapped in a Collapse animation wrapper */
  disableAnimation?: boolean;

  /** How many lines should be shown. Shows a "View More / View Less" toggle to see full message */
  lineLimit?: number;

  /** When the alert is discardable, this is the callback that gets fired on discard */
  onClose?: () => void;

  /** Custom icon to display. Pass false to hide the icon entirely. */
  icon?: React.ReactNode | false;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiAlertProps['sx'];
}

// Helper to map pounce variant icon type
const getIconType = (variant: AlertVariant): string => {
  switch (variant) {
    case 'success':
      return 'check-circle';
    case 'warning':
    case 'error':
      return 'alert-circle-filled';
    case 'info':
    case 'suggest':
      return 'info';
    case 'default':
    default:
      return 'info';
  }
};

/**
 * Pounce Alert component built on top of MUI Alert.
 *
 * An Alert component is simply a container for text that should capture the user's attention.
 * Provides all the functionality from the original Pounce Alert including variants,
 * background styles, discardability, and animations.
 *
 * Features:
 * - Variant support (success, info, warning, error, suggest, default)
 * - Background style variants (solid, transparent) via theme
 * - Discardable with close button
 * - Optional collapse animation
 * - Title and description support
 * - Action buttons support
 * - Line limiting with "View More/Less" toggle
 * - Full pounce style props compatibility
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    title,
    variant = 'default',
    variantBackgroundStyle = 'transparent',
    description,
    discardable = false,
    disableAnimation = false,
    lineLimit,
    actions = null,
    onClose,
    icon,
    children,
    sx,
    ...rest
  },
  ref,
) {
  const { isOpen, close } = useDisclosure({ initialState: true });
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const id = React.useId();

  const handleClose = React.useCallback(() => {
    close();
    if (onClose) {
      onClose();
    }
  }, [close, onClose]);

  // Determine the MUI variant based on pounce variant and background style
  const muiVariant = `${variant}-${variantBackgroundStyle}` as MuiAlertProps['variant'];

  // Get icon type for the variant (only if icon is not explicitly provided)
  const iconType = getIconType(variant);

  // Determine the icon to display: use provided icon (including false), or default icon
  const displayIcon = icon !== undefined ? icon : <Icon type={iconType as IconType} size="large" />;

  const combinedSx = {
    ...pounceStyles,
    ...sx,
  };

  const alertContent = (
    <MuiAlert
      ref={ref}
      variant={muiVariant}
      role="dialog"
      icon={displayIcon}
      action={
        discardable ? (
          <IconButton aria-label="Discard" size="small" onClick={handleClose} icon="close" />
        ) : undefined
      }
      sx={combinedSx}
      {...otherProps}
    >
      {title && (
        <AlertTitle>
          <Box
            as={Heading}
            fontSize={description ? 'large' : 'medium'}
            flexGrow={1}
            mr="auto"
            pr={6}
            id={`${id}-title`}
          >
            {title}
          </Box>
        </AlertTitle>
      )}
      {description && (
        <>
          {lineLimit ? (
            <OverflowTextWithViewMore numLines={lineLimit}>{description}</OverflowTextWithViewMore>
          ) : (
            <Box sx={{ marginTop: title ? 1 : 0 }} fontSize="medium">
              {description}
            </Box>
          )}
        </>
      )}
      {actions && (
        <Flex mt={2} mr={0} ml={0}>
          {typeof actions === 'function' ? actions({ onClose }) : actions}
        </Flex>
      )}
      {children}
    </MuiAlert>
  );

  if (disableAnimation) {
    return isOpen ? alertContent : null;
  }

  return <Collapse isOpen={isOpen}>{alertContent}</Collapse>;
});
